import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatMarkdown } from "@/components/formatMarkdown";
import { Template } from "@/types/template";
import { ExternalLink, MarketingLink } from "@/components/OptionalLink";
import { Badge } from "@/components/ui/badge";
import { ProviderType } from "@/types";
import { useRouter } from "next/router";
import { capitalize } from "@/lib/utils";

export const getTagLabel = (tag: string) => {
  const remap = {
    oss: "OSS",
    nvidia: "NVIDIA",
    ai: "AI",
    openai: "OpenAI",
    "e-learning": "E-Learning",
    "e-commerce": "E-Commerce",
    crm: "CRM",
    cms: "CMS",
    erp: "ERP",
    baas: "BaaS",
    saas: "SaaS",
    paas: "PaaS",
    iaas: "IaaS",
    devops: "DevOps",
    gdpr: "GDPR",
    email: "E-mail",
    rsvp: "RSVP",
    rss: "RSS",
    mcp: "MCP",
    vpn: "VPN",
    ide: "IDE",
    nextjs: "Next.js",
    faq: "FAQ",
    "ai-agent": "AI Agent"
  } as Record<string, string>;

  if (remap[tag]) {
    return remap[tag];
  }

  if (tag.indexOf("-") !== -1) {
    return tag.split("-").map(capitalize).join(" ");
  }

  return capitalize(tag);
};

export const getTemplateSourceLabel = (source?: string) => {
  if (!source) {
    return "";
  }

  if (source.startsWith("file:")) {
    return "Local";
  }

  if (source === "https://github.com/dollardeploy/templates") {
    return "Official";
  }

  if (source.startsWith("user://")) {
    return "User";
  }

  return "Community";
};

export const ListTemplateCard = ({ template }: { template: Template }) => {
  const router = useRouter();
  const provider = router.query.provider as ProviderType | undefined;

  return (
    <Card className="w-[325px] min-h-[300px] max-w-sm flex flex-col">
      <CardHeader>
        <div className="flex flex-col items-center justify-start mb-4">
          <img src={template.logo} alt={`${template.name} logo`} className="h-12 rounded-md" />
        </div>
        <CardTitle className="text-xl mb-4 text-center">{template.name}</CardTitle>
        <CardDescription>
          <div className="mt-2 mb-4 flex flex-wrap gap-2 justify-center">
            {template.experimental ? <Badge variant="destructive">Experimental</Badge> : null}

            {template.tags
              ?.sort()
              .filter((_, index) => index < 2)
              .map(tag => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="hover:bg-primary hover:text-primary-foreground cursor-pointer"
                >
                  <MarketingLink href={`/r?tag=${tag}`}>{getTagLabel(tag)}</MarketingLink>
                </Badge>
              ))}

            {template.tags && template.tags.length > 2 ? (
              <Badge
                variant="outline"
                className="hover:bg-primary hover:text-primary-foreground cursor-pointer"
              >
                <MarketingLink
                  href={`/r/${template.id}${provider ? `?provider=${provider}` : ""}`}
                  className={`plausible-event-name=DeployTemplate plausible-event-template=${template.id}`}
                >
                  + {template.tags.length - 2}
                </MarketingLink>
              </Badge>
            ) : null}
          </div>

          <div className="text-sm prose dark:prose-invert">
            {formatMarkdown(
              template.intro,
              undefined,
              undefined,
              true,
              "text-sm text-secondary-foreground"
            )}
          </div>
        </CardDescription>
      </CardHeader>

      <CardFooter className="flex justify-between mt-auto gap-2">
        <Button asChild size="sm">
          <MarketingLink
            href={`/r/${template.id}${provider ? `?provider=${provider}` : ""}`}
            className={`plausible-event-name=DeployTemplate plausible-event-template=${template.id}`}
          >
            Deploy
          </MarketingLink>
        </Button>

        {template.app?.repositoryUrl ? (
          <Button asChild size="sm" variant="secondary">
            <ExternalLink
              href={
                template.app.sourcePath
                  ? (template.app.repositoryUrl?.endsWith("/")
                      ? template.app.repositoryUrl
                      : template.app.repositoryUrl + "/") +
                    "tree/" +
                    (template.app.sourceBranch ?? "main") +
                    "/" +
                    (template.app.sourcePath?.startsWith("/")
                      ? template.app.sourcePath.slice(1)
                      : template.app.sourcePath)
                  : template.app.repositoryUrl
              }
            >
              Source code
            </ExternalLink>
          </Button>
        ) : null}
      </CardFooter>
    </Card>
  );
};
